import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { useSession } from "@/lib/session";
import { offlineDB, estimateStorage, wipeOfflineDB, type OutboxItem, type DownloadRecord } from "@/lib/offline/db";
import { listOutbox, removeOutboxItem, refreshPendingCount } from "@/lib/offline/outbox";
import { syncAll, getLastSyncAt } from "@/lib/offline/sync-engine";
import { useConnection } from "@/lib/offline/connection";
import { listPacks } from "@/lib/offline/packs";
import type { ContentPack, SyncConflict } from "@/lib/offline/db";
import { Wifi, WifiOff, RefreshCw, Trash2, CloudUpload, HardDrive, AlertTriangle, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/settings/offline")({
  ssr: false,
  head: () => ({ meta: [{ title: "Offline & Sync — MAKAI" }] }),
  component: OfflineSettingsPage,
});

const SETTINGS_KEY = "makai.offline_settings.v1";
type Prefs = {
  wifiOnly: boolean;
  quality: "low" | "medium" | "high";
  storageLimitMb: number;
  autoSyncFreqMin: number;
};
const DEFAULTS: Prefs = { wifiOnly: false, quality: "medium", storageLimitMb: 500, autoSyncFreqMin: 1 };

function loadPrefs(): Prefs {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch { return DEFAULTS; }
}
function savePrefs(p: Prefs) {
  if (typeof window !== "undefined") localStorage.setItem(SETTINGS_KEY, JSON.stringify(p));
}

function OfflineSettingsPage() {
  const { session } = useSession();
  const { state, pending } = useConnection();
  const [outbox, setOutbox] = useState<OutboxItem[]>([]);
  const [downloads, setDownloads] = useState<DownloadRecord[]>([]);
  const [packs, setPacks] = useState<ContentPack[]>([]);
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);
  const [storage, setStorage] = useState<{ usage: number; quota: number } | null>(null);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [prefs, setPrefs] = useState<Prefs>(DEFAULTS);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setOutbox(await listOutbox());
    const db = offlineDB();
    setDownloads(db ? await db.downloads.orderBy("downloaded_at").reverse().toArray() : []);
    setPacks(await listPacks());
    setConflicts(db ? await db.conflicts.orderBy("detected_at").reverse().limit(10).toArray() : []);
    setStorage(await estimateStorage());
    setLastSync(await getLastSyncAt());
    await refreshPendingCount();
  }

  useEffect(() => {
    setPrefs(loadPrefs());
    void refresh();
    const i = setInterval(refresh, 4000);
    return () => clearInterval(i);
  }, []);

  async function handleSyncNow() {
    setBusy(true);
    try { await syncAll(); } finally { setBusy(false); await refresh(); }
  }

  async function handleClearCache() {
    if (!confirm("Clear all cached lessons and dashboard data? Queued offline changes will be kept.")) return;
    const db = offlineDB();
    if (!db) return;
    await db.rows.clear();
    await db.sync_meta.clear();
    await refresh();
  }

  async function handleClearDownloads() {
    if (!confirm("Remove all downloaded content from this device?")) return;
    const db = offlineDB();
    if (!db) return;
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.filter((n) => n.startsWith("makai-downloads")).map((n) => caches.delete(n)));
    }
    await db.downloads.clear();
    await refresh();
  }

  async function handleHardReset() {
    if (!confirm("Wipe ALL offline data on this device? Queued changes will be lost.")) return;
    await wipeOfflineDB();
    await refresh();
  }

  function updatePref<K extends keyof Prefs>(k: K, v: Prefs[K]) {
    const next = { ...prefs, [k]: v };
    setPrefs(next);
    savePrefs(next);
  }

  const userLabel = session ? `${session.name ?? "You"} · ${session.role}` : "Guest";

  return (
    <AppShell role={(session?.role ?? "learner") as "learner" | "teacher" | "parent" | "admin"}>
      <div className="mx-auto w-full max-w-4xl px-4 pb-16 pt-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Link>
        <header className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Offline &amp; Sync</h1>
            <p className="mt-1 text-sm text-muted-foreground">{userLabel}</p>
          </div>
          <StatusPill state={state} pending={pending} />
        </header>

        {/* Sync status */}
        <section className="mt-6 grid gap-3 sm:grid-cols-3">
          <StatCard
            icon={<CloudUpload className="h-4 w-4" />}
            label="Pending changes"
            value={String(pending)}
            tone={pending > 0 ? "amber" : "neutral"}
          />
          <StatCard
            icon={<RefreshCw className="h-4 w-4" />}
            label="Last sync"
            value={lastSync ? new Date(lastSync).toLocaleTimeString() : "Never"}
          />
          <StatCard
            icon={<HardDrive className="h-4 w-4" />}
            label="Storage used"
            value={storage ? `${(storage.usage / 1024 / 1024).toFixed(1)} MB` : "—"}
            sub={storage ? `of ${(storage.quota / 1024 / 1024).toFixed(0)} MB available` : undefined}
          />
        </section>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={handleSyncNow}
            disabled={busy || state === "offline"}
            className="inline-flex items-center gap-2 rounded-lg bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Sync now
          </button>
          <button onClick={handleClearCache} className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm hover:bg-accent">
            <Trash2 className="h-4 w-4" /> Clear cache
          </button>
          <button onClick={handleClearDownloads} className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm hover:bg-accent">
            <Trash2 className="h-4 w-4" /> Clear downloads
          </button>
          <button onClick={handleHardReset} className="inline-flex items-center gap-2 rounded-lg border border-rose-300 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50">
            <AlertTriangle className="h-4 w-4" /> Wipe all offline data
          </button>
        </div>

        {/* Pending queue */}
        <section className="mt-8 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-semibold">Pending changes</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Actions you took offline. They send automatically when you're back online.
          </p>
          {outbox.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              You're all caught up.
            </div>
          ) : (
            <ul className="mt-4 divide-y divide-border">
              {outbox.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        item.status === "failed" ? "bg-rose-100 text-rose-700" :
                        item.status === "syncing" ? "bg-amber-100 text-amber-700" :
                        "bg-muted text-foreground/70"
                      }`}>{item.status}</span>
                      <span className="truncate">{item.op} · {item.table}</span>
                    </div>
                    {item.last_error && (
                      <div className="mt-0.5 truncate text-[11px] text-rose-600" title={item.last_error}>{item.last_error}</div>
                    )}
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {new Date(item.created_at).toLocaleString()} · {item.attempts} attempt{item.attempts === 1 ? "" : "s"}
                    </div>
                  </div>
                  <button
                    onClick={() => item.id != null && removeOutboxItem(item.id).then(refresh)}
                    className="rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-accent"
                  >
                    Discard
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Content packs */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-lg font-semibold">Offline learning packs</h2>
            <Link to="/learner/downloads" className="text-xs font-medium underline underline-offset-2">Manage downloads</Link>
          </div>
          {packs.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">
              No lessons, quizzes or papers downloaded yet.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {packs.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{p.title}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {p.kind.replace("_", " ")} · {(p.size_bytes / 1024).toFixed(0)} KB
                      {p.raw_bytes > p.size_bytes ? ` · compressed ${Math.round((1 - p.size_bytes / p.raw_bytes) * 100)}%` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {conflicts.length > 0 && (
          <section className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-5">
            <h2 className="font-display text-lg font-semibold text-amber-900">Changes we could not apply</h2>
            <p className="mt-1 text-xs text-amber-800">
              These items were updated by your school while this device was offline, so the school's version was kept.
            </p>
            <ul className="mt-3 space-y-1 text-xs text-amber-900">
              {conflicts.map((c) => (
                <li key={c.id}>{new Date(c.detected_at).toLocaleString()} — {c.reason}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Downloads summary */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-semibold">Downloaded for offline</h2>
          {downloads.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Nothing downloaded yet. Open a lesson or library item and tap "Save offline".
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {downloads.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{d.title}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {d.kind} · {(d.size_bytes / 1024 / 1024).toFixed(1)} MB · saved {new Date(d.downloaded_at).toLocaleDateString()}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Preferences */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-semibold">Offline preferences</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Toggle
              label="Download over Wi-Fi only"
              hint="Skip downloads when on mobile data."
              checked={prefs.wifiOnly}
              onChange={(v) => updatePref("wifiOnly", v)}
            />
            <Select
              label="Download quality"
              value={prefs.quality}
              onChange={(v) => updatePref("quality", v as Prefs["quality"])}
              options={[
                { value: "low", label: "Low — smallest files" },
                { value: "medium", label: "Medium (recommended)" },
                { value: "high", label: "High — best quality" },
              ]}
            />
            <NumberInput
              label="Storage limit (MB)"
              value={prefs.storageLimitMb}
              min={100} max={5000} step={50}
              onChange={(v) => updatePref("storageLimitMb", v)}
            />
            <NumberInput
              label="Auto-sync every (minutes)"
              value={prefs.autoSyncFreqMin}
              min={1} max={60} step={1}
              onChange={(v) => updatePref("autoSyncFreqMin", v)}
            />
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function StatusPill({ state, pending }: { state: "online" | "syncing" | "offline"; pending: number }) {
  const Icon = state === "online" ? Wifi : state === "syncing" ? RefreshCw : WifiOff;
  const dot = state === "online" ? "bg-emerald-500" : state === "syncing" ? "bg-amber-400" : "bg-rose-500";
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium">
      <span className={`h-1.5 w-1.5 rounded-full ${dot} ${state === "syncing" ? "animate-pulse" : ""}`} />
      <Icon className={`h-3.5 w-3.5 ${state === "syncing" ? "animate-spin" : ""}`} />
      {state === "online" ? "Online" : state === "syncing" ? "Syncing…" : "Offline"}
      {pending > 0 && <span className="ml-1 rounded-full bg-foreground/10 px-1.5 py-0.5 text-[10px] font-semibold">{pending}</span>}
    </div>
  );
}

function StatCard({ icon, label, value, sub, tone = "neutral" }: { icon: React.ReactNode; label: string; value: string; sub?: string; tone?: "neutral" | "amber" }) {
  return (
    <div className={`rounded-2xl border border-border bg-card p-4 ${tone === "amber" ? "ring-1 ring-amber-200" : ""}`}>
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">{icon} {label}</div>
      <div className="mt-1 font-display text-2xl font-bold tracking-tight">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-3 rounded-xl border border-border p-3">
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="mt-0.5 block text-[11px] text-muted-foreground">{hint}</span>}
      </span>
      <span className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${checked ? "bg-foreground" : "bg-muted"}`}>
        <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className={`inline-block h-4 w-4 transform rounded-full bg-background transition ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
    </label>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function NumberInput({ label, value, min, max, step, onChange }: { label: string; value: number; min: number; max: number; step: number; onChange: (v: number) => void }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium">{label}</span>
      <input
        type="number" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))}
        className="mt-1.5 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
      />
    </label>
  );
}
